import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Box, Flex, IconButton, Image, Select, Text } from "@chakra-ui/react"
import { Swiper, SwiperSlide } from "swiper/react"
import SwiperCore, { Navigation, Pagination } from "swiper"
import { graphql, useStaticQuery } from "gatsby"
import "swiper/css"

import { useRoutes } from "@app/hooks/useRoutes"
import { useConfigContext } from "@app/providers/config"
import { GroupSize, useSwatch } from "@app/hooks/useSwatch"
import { useShopify } from "@app/hooks/useShopify"
import { Icon } from "@app/components/Icon"
import { useGlobalContext } from "@app/providers/global"
import { PDPCallout } from "@app/components/PDPCallout"
import { ProductFormQuantity } from "@app/components/Product/Form/ProductFormQuantity"
import { ProductPairingsAddToCart } from "@app/components/Product/Pairings/ProductPairingsAddToCart"
import { Link } from "@app/components/Link"
import type { ProductCombinedProps, ProductProps } from "@root/types/global"
import { useAnalytics } from "@app/hooks/useAnalytics"

const ITEM_LIST_NAME = "Product Pairings"

type Props = {
  product: ProductCombinedProps
  pairings?: Array<ProductProps>
}

// eslint-disable-next-line react-hooks/rules-of-hooks
SwiperCore.use([Navigation, Pagination])

export const ProductPairings: React.FC<Props> = memo(({ product, pairings }) => {
  const { product: locales } = useStaticQuery<GatsbyTypes.ProductFormAddToCartQuery>(graphql`
    query ProductPairings {
      product: sanityTemplateProduct {
        additionalProductPairingsLabel
      }
    }
  `)

  const { getHandle, getProducts } = useShopify()
  const { additionalPrevious, additionalNext } = useGlobalContext()
  const { trackProductListView } = useAnalytics()

  const containerRef = useRef(null)
  const [swiper, setSwiper] = useState<SwiperCore | null>(null)
  const [inViewport, setInViewport] = useState(false)
  const [items, setItems] = useState<Array<ProductProps> | null>(null)

  const fetchItems = useCallback(async () => {
    const items = await getProducts({ firstImages: 2, firstVariants: 20, handles: pairings?.map(item => getHandle(item)) })

    if (items?.length) setItems(items?.filter(item => item?.availableForSale))
  }, [pairings, getHandle, getProducts])

  const handleItemTracking = useCallback(() => {
    if (inViewport && swiper) {
      trackProductListView([items?.[swiper.activeIndex]], ITEM_LIST_NAME)
    }
  }, [inViewport, items, swiper, trackProductListView])

  // intentionally only run once at first render
  useEffect(() => {
    if (!items && pairings?.length) fetchItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Track when pairings carousel is on screen
  useEffect(() => {
    if (!items?.length) {
      return
    }

    const current = containerRef?.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInViewport(entry.isIntersecting)
      },
      {
        rootMargin: "0px",
      }
    )

    if (current) {
      observer?.observe(current)
    }

    return () => {
      if (current) {
        observer.unobserve(current)
      }
    }
  }, [items?.length])

  // Track whenever carousel enters the viewport
  useEffect(() => {
    if (inViewport) {
      handleItemTracking()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewport])

  if (!pairings?.length || !items?.length) return null
  return (
    <Box ref={containerRef} w="100%" marginTop="4">
      <Flex justifyContent="space-between">
        <Text fontWeight="600" color="grey.text" marginBottom="1" fontSize="sm" letterSpacing="generous">
          {locales?.additionalProductPairingsLabel}
        </Text>
        {items?.length > 1 && (
          <Flex>
            <IconButton
              id={`frequent-${product?.handle}-left`}
              title={additionalPrevious}
              aria-label={additionalPrevious}
              variant="ghost"
              icon={<Icon name="chevron-left" w={5} />}
              w={12}
              p={3}
            />
            <IconButton
              id={`frequent-${product?.handle}-right`}
              title={additionalNext}
              aria-label={additionalNext}
              variant="ghost"
              icon={<Icon name="chevron-right" w={5} />}
              w={12}
              p={3}
            />
          </Flex>
        )}
      </Flex>
      <Box
        bgColor="grey.cloud"
        borderRadius="4px"
        p={{ base: 4, md: 5 }}
        pb={4}
        sx={{
          ".swiper-slide": {
            height: "auto",
          },
        }}
      >
        <Swiper
          autoHeight={true}
          navigation={{ prevEl: `#frequent-${product?.handle}-left`, nextEl: `#frequent-${product?.handle}-right` }}
          pagination={{ clickable: true, el: `#frequent-${product?.handle}-pagination`, type: "bullets" }}
          slidesPerView={1}
          spaceBetween={32}
          onSwiper={swiper => setSwiper(swiper)}
          onSlideChange={handleItemTracking}
        >
          {items.map((pairing, index) => (
            <SwiperSlide key={index}>
              <ProductPairingItem pairing={pairing} index={index} />
            </SwiperSlide>
          ))}
        </Swiper>
        <Box
          id={`frequent-${product?.handle}-pagination`}
          pb={0}
          sx={{
            ".swiper-pagination-bullet-active": {
              background: "#767676 !important",
            },
            ".swiper-pagination-bullet": {
              w: 2,
              h: 2,
              opacity: 1,
              background: "grey.white",
            },
          }}
        />
      </Box>
    </Box>
  )
})

type ProductPairingItemProps = {
  pairing: ProductProps
  index: number
}

export const ProductPairingItem: React.FC<ProductPairingItemProps> = memo(({ pairing, index }) => {
  const { urlResolver } = useRoutes()
  const { trackProductClick } = useAnalytics()
  const {
    settings: { routes },
  } = useConfigContext()

  const productVariants = pairing?.variants?.filter(variant => !!Number(variant?.priceV2?.amount || "0")) || []

  const { getGroupSizes } = useSwatch()
  const [groupSizes, setGroupSizes] = useState<GroupSize[]>([])
  const firstAvailableVariant = productVariants?.find(variant => variant.availableForSale) || productVariants?.[0]
  const [activeVariant, setActiveVariant] = useState(firstAvailableVariant)
  useEffect(() => {
    ;(async () => {
      if (pairing) {
        const groups = await getGroupSizes(pairing)
        if (groups) setGroupSizes(groups)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [quantity, setQuantity] = useState(1)

  const defaultOptions =
    activeVariant?.selectedOptions ||
    pairing?.options?.map(({ name }) => ({
      name,
      value: firstAvailableVariant ? firstAvailableVariant.selectedOptions.find(opt => opt.name === name) : "",
    }))
  const [selectedOptions, setSelectedOptions] = useState(defaultOptions)

  const handleQuantity = useCallback(quantity => (quantity > -1 ? setQuantity(quantity) : null), [setQuantity])

  const handleVariantOption = useCallback(
    e => {
      const option = { value: e.target.value, name: e.target.name }
      const newOption = selectedOptions?.map(selectedOption => (selectedOption.name === option.name ? option : selectedOption))
      setSelectedOptions(newOption)
      const newActiveVariant = productVariants.find(
        variant =>
          variant?.selectedOptions?.filter(
            selectedOption =>
              selectedOption.value ===
              (selectedOption.name === option.name
                ? option.value
                : selectedOptions.find(option => option.name === selectedOption.name)?.value)
          )?.length === variant?.selectedOptions?.length
      )
      setActiveVariant(newActiveVariant)
      setQuantity(1)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pairing.variants, selectedOptions]
  )

  const handleGroupOption = useCallback(
    e => {
      const option = { value: e.target.value, name: e.target.name }
      const newActiveVariant = groupSizes.find(({ key }) => key === option.value)?.variant
      setActiveVariant(newActiveVariant)
    },
    [groupSizes]
  )

  const selectableOptions = useMemo(
    () =>
      pairing.options
        .filter(({ values }) => values?.length > 1)
        .map(({ name, values }) => ({
          name,
          values: values
            ?.map(value => ({
              variant: productVariants.find(
                variant =>
                  variant?.selectedOptions?.filter(
                    selectedOption =>
                      selectedOption.value ===
                      (selectedOption.name === name ? value : selectedOptions.find(option => option.name === selectedOption.name)?.value)
                  )?.length === variant?.selectedOptions?.length
              ),
              value,
            }))
            ?.filter(({ variant }) => !!variant),
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pairing.options, pairing.variants, selectedOptions]
  )

  const link = urlResolver(pairing, routes.PRODUCT)

  const { formatMoney } = useShopify()

  const stripZeroDecimals = (priceString: string | undefined) => {
    if (!priceString) return priceString
    return priceString.replace(/\.00$/, "")
  }

  const getPrice = variant => {
    if (!variant?.priceV2) return ""
    if (variant?.priceV2?.amount) return stripZeroDecimals(formatMoney(Number(variant?.priceV2?.amount) * quantity))
    if (typeof variant?.priceV2 == "string") return stripZeroDecimals(formatMoney(Number(variant?.priceV2) * quantity))
  }
  const getCompareAtPrice = variant => {
    if (!variant?.compareAtPriceV2) return ""
    if (variant?.compareAtPriceV2?.amount) return stripZeroDecimals(formatMoney(Number(variant?.compareAtPriceV2?.amount) * quantity))
    if (typeof variant?.compareAtPriceV2 == "string") return stripZeroDecimals(formatMoney(Number(variant?.compareAtPriceV2) * quantity))
  }

  const handleLinkClick = useCallback(() => {
    trackProductClick(pairing, activeVariant, index, ITEM_LIST_NAME)
  }, [activeVariant, index, pairing, trackProductClick])

  return (
    <Flex flexDir="column" w="100%" h="full">
      <Flex gap={4} mb={4} w="100%">
        <Box as={Link} w="40%" to={link.url} onClick={handleLinkClick}>
          <Image {...pairing?.images?.[0]} key={pairing?.images?.[0]?.src} w="full" />
        </Box>
        <Flex flexDir="column" gap={2} w="60%">
          <PDPCallout tags={pairing?.tags} fontSize={12} letterSpacing="generous" lineHeight={"18px"} padding="6px" />
          <Text color="grey.text" fontWeight={600} fontSize="sm" lineHeight="24px">
            {activeVariant?.product?.title || pairing?.title}
          </Text>
          {!groupSizes?.length &&
            selectableOptions?.map(
              ({ name, values }) =>
                values?.length > 1 && (
                  <Select
                    key={name}
                    name={name}
                    onChange={handleVariantOption}
                    textTransform="capitalize"
                    variant="newOutline"
                    w="auto"
                    value={selectedOptions.find(op => op.name === name)?.value}
                  >
                    {values?.map(({ value }) => (
                      <option key={value} value={value}>
                        {`${value}`}
                      </option>
                    ))}
                  </Select>
                )
            )}
          {groupSizes.length > 1 && (
            <Select name={"size"} onChange={handleGroupOption} textTransform="capitalize" variant="newOutline" w="auto">
              {groupSizes.map(({ key, variant }) => (
                <option key={key} value={key}>
                  {variant.title}
                </option>
              ))}
            </Select>
          )}
          <Flex gap={1} marginTop="auto">
            {activeVariant?.compareAtPriceV2 && (
              <Text color="grey.darker" fontWeight={500} fontSize="xl" marginTop="auto" lineHeight="32px" textDecoration="line-through">
                {getCompareAtPrice(activeVariant)}
              </Text>
            )}
            <Text color="grey.text" fontWeight={500} fontSize="xl" marginTop="auto" lineHeight="32px">
              {getPrice(activeVariant)}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Flex gap={4} mb={4} mt="auto">
        <ProductFormQuantity
          value={quantity}
          handleChange={handleQuantity}
          max={activeVariant?.quantityAvailable}
          backgroundColour="grey.white"
        />
        <ProductPairingsAddToCart quantity={quantity} activeVariant={activeVariant} />
      </Flex>
    </Flex>
  )
})
